import React, { useEffect, useState } from "react";
import { collection, deleteDoc, doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../firestore-DB-config/firebase-config"; // Adjust the path based on your firebase config
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Grid2,
  CircularProgress,
  Box,
  IconButton,
  Container,
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import { Link } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Blog Interface
interface Blog {
  id: string;
  date: string;
  title: string;
  cardDescription: string;
  description: string;
  author: string;
  datePublished: string;
  blogImageUrl: string;
  authorImageUrl: string;
  slug: string;
}

const BlogList: React.FC = () => {
  const [blogs, setBlogs] = useState<Blog[]>([]); // Store blog posts from Firestore
  const [loading, setLoading] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);
  const [selectedBlog, setSelectedBlog] = useState<string>("");

  // Fetch blogs from Firestore (real-time updates)
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "blogs"), (snapshot) => {
      const blogList: Blog[] = snapshot.docs.map((doc) => ({
        id: doc.id, // Firestore document ID
        ...doc.data(), // Spread other fields
      })) as Blog[];
      setBlogs(blogList);
      setLoading(false); // Data is loaded, stop showing the spinner
    });

    return () => unsubscribe();
  }, []);

  // Function to open the confirmation dialog
  const handleClickOpen = (blogId: string) => {
    setSelectedBlog(blogId);
    setOpen(true);
  };

  // Function to close the dialog
  const handleClose = () => {
    setOpen(false);
  };

  // Confirm and delete blog
  const handleConfirmDelete = async () => {
    try {
      await deleteDoc(doc(db, "blogs", selectedBlog));
      toast.success("Blog deleted successfully");
    } catch (error) {
      toast.error("Error deleting blog");
      console.error("Error deleting blog:", error);
    }
    handleClose(); // Close the dialog after the action
  };

  return (
    <>
      <Header />
      <Container sx={{ mb: "50px" }}>
        <Box sx={{ padding: 3 }}>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{
              fontSize: { xs: "24px", md: "32px" },
              color: "rgba(1, 29, 51, 1)",
              my: "30px",
            }}
          >
            Delete Blogs
          </Typography>
          {loading ? (
            <CircularProgress />
          ) : (
            <Grid2 container spacing={6}>
              {blogs.length > 0 ? (
                blogs.map((blog) => {
                  // Extract day and weekday from the datePublished field
                  const date = new Date(blog.datePublished);
                  const day = date.getDate(); // Get the day of the month (e.g., 3)
                  const weekday = date.toLocaleDateString("en-US", {
                    weekday: "short",
                  }); // Get short form of the weekday (e.g., Thu)

                  return (
                    <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={blog.id}>
                      <Card
                        sx={{
                          width: { xs: "280px", sm: "300px", md: "320px" },
                          height: { xs: "92%", sm: "100%" },
                          borderRadius: "16px",
                          boxShadow: 3,
                          transition: "transform 0.3s, box-shadow 0.3s",
                          "&:hover": {
                            transform: "scale(1.05)", // Slightly enlarges the card on hover
                            boxShadow: 6, // Increases shadow depth for a 3D effect
                          },
                          margin: { xs: "16px 0", sm: "auto" }, // Add margin for small screens
                          position: "relative", // For positioning the delete button
                        }}
                      >
                        <Link
                          to={`/blog/${blog.slug}`}
                          style={{ textDecoration: "none", color: "inherit" }}
                          onClick={() => window.scrollTo(0, 0)}
                        >
                          <Box position="relative">
                            <CardMedia
                              component="img"
                              image={blog.blogImageUrl}
                              alt={blog.title}
                              sx={{
                                borderRadius: "8px 8px 0 0",
                                height: { xs: 180, sm: 220, md: 250 }, // Responsive image height
                                objectFit: "cover", // Ensure the image covers the entire area without distortion
                              }}
                            />

                            {/* Sticky Note for Date */}
                            <Box
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 40,
                                backgroundColor: "#fff",
                                borderRadius: "0 0 8px 8px", // Rounded corners
                                padding: "4px 10px",
                                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)", // Subtle shadow
                                textAlign: "center",
                                width: "50px", // Adjust to match the sticky note size
                                height: "55px", // Fixed height to match the design
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center", // Centers the text vertically
                              }}
                            >
                              <Typography
                                variant="h6"
                                fontWeight="bold"
                                lineHeight={1.2}
                              >
                                {day} {/* Date */}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {weekday} {/* Weekday */}
                              </Typography>
                            </Box>
                          </Box>

                          <CardContent>
                            <Typography
                              gutterBottom
                              variant="h6"
                              component="div"
                              fontWeight="bold"
                              mt={1}
                              sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }} // Adjust title size for small screens
                            >
                              {blog.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: { xs: "0.8rem", sm: "1rem" } }} // Adjust description font size for small screens
                            >
                              {blog.cardDescription}
                            </Typography>

                            <Box
                              display="flex"
                              alignItems={{ xs: "flex-start", sm: "center" }}
                              justifyContent="space-between"
                              mt={4}
                            >
                              <Box
                                display="flex"
                                alignItems="center"
                                mb={{ xs: 2, sm: 0 }} // Add bottom margin on small screens
                              >
                                <Avatar
                                  src={blog.authorImageUrl}
                                  sx={{ width: 40, height: 40, mr: 1 }}
                                />
                                <Box>
                                  <Typography variant="body2" fontWeight="bold">
                                    {blog.author}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                    }} // Adjust datePublished font size for small screens
                                  >
                                    {blog.datePublished}
                                  </Typography>
                                </Box>
                              </Box>

                              <Button
                                sx={{
                                  background:
                                    "linear-gradient(180deg, #014167 -11.96%, #011D33 40.48%)",
                                  color: "white",
                                  borderRadius: "50%",
                                  width: 47,
                                  height: 47,
                                  minWidth: 47, // Ensure button doesn't stretch horizontally
                                  padding: 0, // Adjust padding so the content fits snugly
                                  transition:
                                    "background-color 0.3s, transform 0.3s",
                                  "&:hover": {
                                    backgroundColor: "#1976d2", // Change button color on hover
                                    transform: "scale(1.1)", // Slightly enlarges the button
                                  },
                                  alignSelf: {
                                    xs: "flex-start",
                                    sm: "flex-end",
                                  }, // Align button on small screens
                                }}
                              >
                                <ArrowForwardIcon sx={{ fontSize: 24 }} />{" "}
                                {/* Adjust icon size */}
                              </Button>
                            </Box>
                          </CardContent>
                        </Link>
                        
                        {/* Delete button */}
                        <IconButton
                          sx={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            color: "error.main",
                            backgroundColor: "rgba(255, 255, 255, 0.8)",
                            borderRadius: "50%",
                            boxShadow: 1,
                            "&:hover": {
                              backgroundColor: "rgba(255, 0, 0, 0.7)",
                            },
                          }}
                          onClick={() => handleClickOpen(blog.id)}
                        >
                          <DeleteIcon sx={{ fontSize: 36 }} />
                        </IconButton>
                      </Card>
                    </Grid2>
                  );
                })
              ) : (
                <Typography>No Blogs found.</Typography>
              )}
            </Grid2>
          )}
        </Box>
      </Container>
      <Footer />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Material-UI Dialog for confirmation */}
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="confirm-delete-title"
        aria-describedby="confirm-delete-description"
      >
        <DialogTitle id="confirm-delete-title">Delete Blog</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-delete-description">
            Are you sure you want to delete this blog? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BlogList;
